
import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://submit.jotform.com/242885547659166', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Eric Javidi",
    "description": "Have a question about Houston history? Know of a story that should be included? Want to suggest corrections or additions? Send a message.",
    "url": `${import.meta.env.VITE_SITE_URL}/contact`,
    "mainEntity": {
      "@type": "Person",
      "name": "Eric Javidi",
      "jobTitle": "Houston Storyteller & Guide Writer"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <SEO
        title="Contact Eric Javidi - Houston Guide Questions & Suggestions"
        description="Have a question about Houston history? Know of a story that should be included? Want to suggest corrections or additions? Contact Eric Javidi about Houston guides."
        keywords="contact Eric Javidi, Houston history questions, Houston guide suggestions, Houston story submissions"
        ogTitle="Contact Eric Javidi - Houston Guide Questions & Suggestions"
        ogDescription="Have a question about Houston history? Know of a story that should be included? Contact Eric Javidi."
        ogUrl={`${import.meta.env.VITE_SITE_URL}/contact`}
        twitterTitle="Contact Eric Javidi - Houston Guide Questions & Suggestions"
        twitterDescription="Have a question about Houston history? Know of a story that should be included? Contact Eric Javidi."
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="bg-gradient-to-r from-emerald-900 via-teal-900 to-emerald-800 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-xl font-bold text-white flex items-center">
                <div className="w-8 h-8 bg-emerald-400 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-compass-3-line text-emerald-900"></i>
                </div>
                Eric Javidi
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <Link to="/" className="text-emerald-200 hover:text-emerald-100 px-3 py-2 text-sm font-medium transition-colors">
                  Home
                </Link>
                <Link to="/about" className="text-emerald-200 hover:text-emerald-100 px-3 py-2 text-sm font-medium transition-colors">
                  About
                </Link>
                <Link to="/guides" className="text-emerald-200 hover:text-emerald-100 px-3 py-2 text-sm font-medium transition-colors">
                  Guides
                </Link>
                <Link to="/resources" className="text-emerald-200 hover:text-emerald-100 px-3 py-2 text-sm font-medium transition-colors">
                  Resources
                </Link>
                <Link to="/contact" className="text-white bg-emerald-700 rounded-md px-3 py-2 text-sm font-medium">
                  Contact
                </Link>
              </div>
            </div>
            <div className="md:hidden">
              <button className="text-emerald-200 hover:text-white">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header Hero */}
      <section 
        className="py-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 relative overflow-hidden"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/3546f80239df9c07949d3647f8f55992.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-800/90 via-teal-800/85 to-emerald-700/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <i className="ri-mail-line text-4xl text-white"></i>
              </div>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-emerald-100 leading-relaxed">
              Have a question about Houston history? Know of a story that should be included? Want to suggest corrections or additions? Send a message.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Form */}
            <div>
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-emerald-100">
                <div className="mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-message-3-line text-3xl text-white"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">Send a Message</h2>
                  <p className="text-center text-gray-600">Share your Houston stories and questions</p>
                </div>

                <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
                          placeholder="Your name"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm pr-8"
                      >
                        <option value="">Select a topic</option>
                        <option value="question">Question about a guide</option>
                        <option value="correction">Correction or update</option>
                        <option value="suggestion">Story suggestion</option>
                        <option value="collaboration">Collaboration</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                        Message * (500 characters max)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        maxLength={500}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm resize-vertical"
                        placeholder="Your message..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1 text-right">
                        {formData.message.length}/500 characters
                      </p>
                    </div>

                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 px-6 rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-600 focus:outline-none focus:ring-4 focus:ring-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center">
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center">
                            <i className="ri-send-plane-line mr-2"></i>
                            Send Message
                          </span>
                        )}
                      </button>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                        <div className="flex items-center">
                          <i className="ri-check-line text-green-600 mr-2"></i>
                          <p className="text-green-800 text-sm font-medium">
                            Thank you for your message! I'll get back to you soon.
                          </p>
                        </div>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                        <div className="flex items-center">
                          <i className="ri-error-warning-line text-red-600 mr-2"></i>
                          <p className="text-red-800 text-sm font-medium">
                            Please check that all required fields are filled and your message is under 500 characters.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Contact Image & Info */}
            <div className="space-y-8">
              <div 
                className="h-80 bg-cover bg-center rounded-2xl shadow-lg relative overflow-hidden"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20downtown%20skyline%20at%20golden%20hour%20with%20modern%20skyscrapers%20reflecting%20warm%20sunlight%2C%20iconic%20city%20view%20showing%20the%20energy%20and%20growth%20of%20Houston%20Texas%2C%20professional%20cityscape%20photography%20with%20clear%20blue%20sky%20and%20impressive%20urban%20architecture&width=600&height=500&seq=contact-houston-skyline&orientation=landscape')`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/80 to-teal-600/80"></div>
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <div className="text-center text-white">
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-user-heart-line text-4xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Eric Javidi</h3>
                    <p className="text-emerald-100">Houston Storyteller & Guide Writer</p>
                  </div>
                </div>
              </div>

              {/* Contact Methods */}
              <div className="grid gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100 hover:shadow-xl transition-shadow">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mr-4">
                      <i className="ri-time-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Response Time</h3>
                      <p className="text-gray-600">Typically within 2-3 business days</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100 hover:shadow-xl transition-shadow">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mr-4">
                      <i className="ri-question-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Best For</h3>
                      <p className="text-gray-600">Questions, corrections, story suggestions, local knowledge</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg border border-emerald-100 hover:shadow-xl transition-shadow">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mr-4">
                      <i className="ri-shield-check-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Privacy</h3>
                      <p className="text-gray-600">Your information is only used to respond to your message</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Contribution Section */}
      <section className="py-20 bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20community%20gathering%20with%20diverse%20residents%20sharing%20local%20stories%20and%20historical%20knowledge%2C%20neighborhood%20meeting%20with%20people%20of%20different%20ages%20discussing%20Houston%20heritage%2C%20collaborative%20atmosphere%20with%20maps%20and%20documents%20on%20table%2C%20multicultural%20community%20engagement&width=1200&height=600&seq=community-bg&orientation=landscape')`
          }}
        ></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="w-20 h-20 bg-emerald-500/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-community-line text-4xl text-emerald-400"></i>
            </div>
            <h2 className="text-4xl font-bold mb-6">
              Your Houston Stories Matter
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              These guides are strengthened by community knowledge. Your corrections, suggestions, and local stories help create more accurate and complete narratives of Houston's neighborhoods.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-5

0 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-edit-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Corrections & Updates</h3>
              <p className="text-gray-300 leading-relaxed">
                Spotted an error or outdated information? Help keep the guides accurate and current.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-ful

l flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Story Suggestions</h3>
              <p className="text-gray-300 leading-relaxed">
                Know of hidden gems, untold stories, or overlooked neighborhoods that deserve exploration?
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border

 border-white/20 hover:bg-white/15 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items

-center justify-center mx-auto mb-6">
                <i className="ri-handshake-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Local Expertise</h3>
              <p className="text-gray-300 leading-relaxed">
                Share your neighborhood knowledge and help others discover Houston through your perspective.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-100 via-teal-50 to-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-12 border border-emerald-100">
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded

-full flex items-center justify-center mx-auto mb-8">
              <i className="ri-compass-discover-line text-4xl text-white"></i>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Ready to Explore Houston?
            </h2>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              While you're here, browse the latest guides to Houston's neighborhoods, outdoor spaces, and cultural experiences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/guides"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-lg font-semibold rounded-full hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Browse All Guides
                <i className="ri-map-line ml-3 text-xl"></i>
              </Link>
              <Link
                to="/resources"
                className="inline-flex items-center px-8 py-4 border-2 border-emerald-500 text-emerald-600 text-lg font-semibold rounded-full hover:bg-emerald-50 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                View Resources
                <i className="ri-external-link-line ml-3 text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-compass-3-line text-white text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold">Eric Javidi</h3>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Houston-based storyteller exploring where the city's history meets its natural landscapes.
              </p>
              <div className="flex items-center space-x-4">
                <a
                  href="https://www.youtube.com/@EricJavidi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-red-400 transition-colors"
                  aria-label="YouTube Channel"
                >
                  <i className="ri-youtube-fill text-2xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-6 text-lg">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-300 hover:text-emerald-400 transition-colors">About</Link></li>
                <li><Link to="/guides" className="text-gray-300 hover:text-emerald-400 transition-colors">Guides</Link></li>
                <li><Link to="/resources" className="text-gray-300 hover:text-emerald-400 transition-colors">Resources</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-emerald-400 transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-6 text-lg">Connect</h4>
              <ul className="space-y-3">
                <li><a href="https://www.youtube.com/@EricJavidi" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-emerald-400 transition-colors">YouTube Channel</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="text-gray-300 hover:text-emerald-400 transition-colors">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-12 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 Eric Javidi. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
